```vue
<template>
  <v-container fluid class="width-100" v-if="getPermission('setting:view')">
    <v-layout>
      <v-flex>
        <h3
          class="font-weight-700 mb-0 font-size-24 custom-headline color-custom-blue"
        >
          Purchase Order
        </h3>
      </v-flex>

      <v-flex class="text-right">
        <v-btn
          depressed
          class="custom-grey-border custom-bold-button"
          @click="goBack"
        >
          <v-icon small left>mdi-keyboard-backspace</v-icon>
          Back
        </v-btn>
      </v-flex>
    </v-layout>

    <v-card flat class="custom-grey-border remove-border-radius mt-4">
      <v-card-title class="headline grey lighten-4">
        <v-row>
          <v-col md="6" class="py-0 my-auto">
            <h3
              class="font-weight-700 custom-headline color-custom-blue"
              style="margin: 3.5px 0"
            >
              PO Category
            </h3>
          </v-col>

          <v-col md="6" class="text-right py-0">
            <v-btn
              v-if="getPermission('setting:update')"
              depressed
              small
              color="cyan"
              class="white--text custom-bold-button"
              :disabled="loading"
              @click="openCategoryDialog()"
            >
              <v-icon small left>mdi-plus</v-icon>
              CREATE
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text class="p-6">
        <v-simple-table class="simple-table">
          <thead>
            <tr>
              <th class="simple-table-th text-center" width="50">#</th>
              <th class="simple-table-th text-left">Category Name</th>
              <th class="simple-table-th text-center" width="120">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(category, index) in categories" :key="category.id">
              <td class="simple-table-td text-center">
                {{ index + 1 }}
              </td>

              <td class="simple-table-td text-left">
                <span
                  class="category-color-dot mr-2"
                  :style="{
                    backgroundColor: category.color || '#00bcd4',
                  }"
                ></span>

                {{ category.text }}
                <v-chip
                  v-if="category.quotation_linkage_mandatory === true"
                  x-small
                  outlined
                  color="cyan darken-2"
                  class="ml-2 font-weight-600 text-no-wrap"
                >
                  Quotation required
                </v-chip>
              </td>

              <td class="simple-table-td text-center">
                <v-icon
                  v-if="getPermission('setting:update')"
                  small
                  color="cyan"
                  class="mr-3"
                  :disabled="category.is_exist || loading"
                  @click="openCategoryDialog(category)"
                >
                  mdi-pencil
                </v-icon>

                <v-icon
                  v-if="getPermission('setting:update')"
                  small
                  color="red"
                  :disabled="category.is_exist || loading"
                  @click="deleteCategory(category)"
                >
                  mdi-delete
                </v-icon>
              </td>
            </tr>

            <tr v-if="!loading && !categories.length">
              <td colspan="3" class="py-4 text-center grey--text">
                No PO categories found.
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>

      <Dialog :common-dialog="dialog" :dialog-width="700">
        <template v-slot:title>
          {{ form.id ? "Edit" : "Add" }} PO Category
        </template>

        <template v-slot:body>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row class="po-category-options">
              <v-col cols="12" md="12">
                <label class="font-weight-500 d-block required mb-2">
                  Category Name
                </label>
                <v-text-field
                  v-model.trim="form.text"
                  placeholder="Enter category name"
                  :rules="[(value) => !!value || 'Category name is required']"
                  :maxlength="100"
                  dense
                  filled
                  solo
                  flat
                  color="cyan"
                ></v-text-field
              ></v-col>
              <v-col cols="12" md="6">
                <label class="font-weight-500 d-block mb-2"> Color </label>

                <v-color-picker
                  v-model.trim="form.color"
                  :swatches="swatches"
                  show-swatches
                  hide-canvas
                  hide-inputs
                  swatches-max-height="100"
                  elevation="2"
                ></v-color-picker>
              </v-col>

              <v-col cols="12" md="6" class="pt-8">
                <v-switch
                  v-model="form.quotation_linkage_mandatory"
                  label="Is Quotation linkage mandatory?"
                  inset
                  color="cyan"
                  :true-value="1"
                  :false-value="0"
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </template>

        <template v-slot:action>
          <v-btn
            color="cyan"
            depressed
            class="white--text custom-bold-button"
            :disabled="loading || !valid"
            :loading="loading"
            @click="saveCategory"
          >
            Save
          </v-btn>

          <v-btn
            text
            class="custom-bold-button"
            :disabled="loading"
            @click="dialog = false"
          >
            Close
          </v-btn>
        </template>
      </Dialog>
    </v-card>
  </v-container>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import ValidationMixin from "@/core/plugins/validation-mixin";
import { DELETE, GET, PATCH, POST, } from "@/core/services/store/request.module";
export default {
  name: "profile-purchase-order-setting",
  mixins: [ValidationMixin],
  components: {Dialog},

  data() {
    return {
      categories: [],
      dialog: false,
      loading: false,
      valid: false,
      form: this.emptyForm(),

      swatches: [
        ["#FF0000", "#AA0000", "#550000"],
        ["#FFFF00", "#AAAA00", "#555500"],
        ["#00FF00", "#00AA00", "#005500"],
        ["#00FFFF", "#00AAAA", "#005555"],
        ["#0000FF", "#0000AA", "#000055"],
      ],
    };
  },

  methods: {
    emptyForm() {
      return {
        id: null,
        text: "",
        color: "#00bcd4",
        quotation_linkage_mandatory: 0,
      };
    },

    loadCategories() {
      this.loading = true;

      this.$store
        .dispatch(GET, {
          url: "purchase-order/categories",
        })
        .then(({ data }) => {
          this.categories = Array.isArray(data) ? data : [];
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },

    openCategoryDialog(category) {
      if (category && category.is_exist) return;

      this.form = category
        ? {
            ...this.emptyForm(),
            ...category,
            quotation_linkage_mandatory: category.quotation_linkage_mandatory
              ? 1
              : 0,
          }
        : this.emptyForm();

      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.resetValidation();
      });
    },

    saveCategory() {
      if (this.form.id && this.form.is_exist) return;
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      const request = this.form.id
        ? this.$store.dispatch(PATCH, {
            url: `purchase-order/categories/${this.form.id}`,
            data: this.form,
          })
        : this.$store.dispatch(POST, {
            url: "purchase-order/categories",
            data: this.form,
          });

      request
        .then(() => {
          this.dialog = false;
          this.loadCategories();
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },

    deleteCategory(category) {
      if (category.is_exist || this.loading) return;

      this.loading = true;

      this.$store
        .dispatch(DELETE, {
          url: `purchase-order/categories/${category.id}`,
        })
        .then(() => this.loadCategories())
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
  },

  mounted() {
    this.loadCategories();
  },
};
</script>

<style scoped>
.category-color-dot {
  display: inline-block;
  width: 12px;
  height: 20px;
  border-radius: 4px;
  vertical-align: middle;
}
</style>
``` **Important:** In your original `PurchaseOrder.vue`, you have
`@click="goBack"`, but the `goBack()` method was not included in the code you
provided. If `goBack()` comes from a mixin/base component, you're fine. If it
was defined elsewhere in the original file, **keep that method when merging**.
